import type {JSX} from 'react'

import Tooltip from './Tooltip'
import {classNames} from '../../utils'

export interface RadioButtonsOption<T> {
  value: T
  display: string
  disabled?: boolean
  title?: string
  disabledReasonTooltip?: string
  tooltip?: string
}

interface RadioButtonsProps<T> {
  name: string
  value: T
  options: Array<RadioButtonsOption<T>>
  onChange: (value: T) => void
  disabled?: boolean
}

export default function RadioButtons<T extends string | number | readonly string[] | null | undefined>(props: Readonly<RadioButtonsProps<T>>): JSX.Element {
  const set = (value: T) => {
    if (props.disabled) {
      return
    }

    props.onChange(value)
  }

  const isDisabled = !!props.disabled
  const classes = {
    'form-check form-check-inline': true,
    disabled: isDisabled
  }

  const radioButtons = []
  for (const opt of props.options) {
    const showDisabledReasonToolTip = opt.disabled && opt.disabledReasonTooltip
    const showTooltip = !!opt.tooltip
    const id = `${props.name}_${String(opt.value)}`
    const testId = `${props.name}-${String(opt.value)}`

    radioButtons.push(
      <div className={classNames(classes)} key={String(opt.value)}>
        <input
          data-testid={testId}
          className='form-check-input'
          disabled={isDisabled || opt.disabled}
          type='radio'
          name={props.name}
          id={id}
          value={opt.value ?? undefined}
          checked={props.value === opt.value}
          onChange={(e) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- this should be fine
            set(e.target.value as T)
          }}
          title={opt.title}
        />
        <label className='form-check-label' htmlFor={id}
               title={opt.title}>{opt.display}</label>
        {showDisabledReasonToolTip ? <Tooltip title={opt.disabledReasonTooltip ?? ''}/> : null}
        {showTooltip ? <Tooltip title={opt.tooltip ?? ''}/> : null}
      </div>
    )
  }

  return (
    <div className='mb-3'>
      {radioButtons}
    </div>
  )
};
